import { storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc';
import { colorStyle, hoverColorStyle, styleOverrides } from '@/lib/helpers';
import { getRequestContext } from '@/lib/request-context';

/**
 * Flexible styleable container. Carries colour, per-side padding and radius,
 * per-item placement (col-span / align / width / max-width) and an optional
 * inner flex layout, so each box in a grid can be styled and placed on its own.
 *
 * Layout values are Tailwind classes (stored in the schema so Tailwind picks them
 * up); colour and radius come through the inline-style helpers.
 */
const Box = ({ blok }) => {
  // Language visibility (server-side): if this box is marked hidden for the
  // current language, render nothing so it's left out of the page entirely.
  // The language is set per-request by the locale page (see request-context.js).
  const hideLanguages = blok.hide_languages || [];
  if (hideLanguages.length && hideLanguages.includes(getRequestContext().language)) {
    return null;
  }

  // `gap`, `align_items` and `justify_content` only do anything on a flex
  // container, but they live on their own tab and read as independent settings —
  // so they were routinely set with no Inner Layout and silently discarded.
  // Treat any of them as implying a flex box, defaulting to a column: block
  // layout already stacks vertically, so flex-col preserves the existing flow
  // and simply honours the spacing that was asked for. (Defaulting to flex
  // unconditionally would instead restretch children of every plain box.)
  const direction = blok.direction
    || ((blok.gap || blok.align_items || blok.justify_content) ? 'flex-col' : '');
  // "flex-row-nowrap" is a sentinel for a Row that doesn't wrap; map it to the
  // real classes. Plain "flex-row" wraps by default.
  const isRow = direction === 'flex-row' || direction === 'flex-row-nowrap';
  const flexDir = isRow ? 'flex-row' : direction;
  const wrap = direction === 'flex-row' ? 'flex-wrap' : direction === 'flex-row-nowrap' ? 'flex-nowrap' : '';

  // The box is a flex container when an Inner Layout is set, otherwise a plain
  // block div. The Visibility tab needs display-aware classes: `hidden` and
  // `flex`/`block` all set `display`, so a hidden box must drop its base display
  // and restore the RIGHT one at the breakpoint (never both on one element —
  // which wins would depend on stylesheet order, not class order).
  const baseDisplay = direction ? 'flex' : ''; // block is the div default
  const restore = direction ? 'lg:flex' : 'lg:block';
  // Literal strings below so Tailwind's scanner picks them up.
  const visibility =
    blok.visibility === 'hide-below-lg' ? ['hidden', restore] // hide < lg, show ≥ lg
    : blok.visibility === 'hide-from-lg' ? [baseDisplay, 'lg:hidden'] // show < lg, hide ≥ lg
    : [baseDisplay];

  const inner = direction
    ? [flexDir, wrap, blok.gap || 'gap-4', blok.align_items || '', blok.justify_content || '']
    : [];

  // Hover/focus colours ride along as custom properties plus one utility class
  // per field that is set (see app/styles/hover-colors.css) — they also light
  // up while this box sits inside the selected option of a Toggle.
  const hover = hoverColorStyle(blok);

  const className = [
    ...visibility,
    blok.col_span || '',
    blok.align_self || '',
    blok.justify_self || '',
    // Width is explicit because a box has no width of its own: as a flex item
    // (inside a Row box, or a Comparison Table cell) it shrinks to its content,
    // and max-width only caps it. "Full" is the way to make it span its parent.
    blok.width || '',
    blok.max_width || '',
    // A capped box centres itself, the same way Section and Grid do. Without
    // this it pins to the left of its parent, which only shows up once the
    // viewport is wider than the cap — so it read as correct on small screens
    // and off-centre on large ones.
    blok.max_width ? 'mx-auto' : '',
    ...inner,
    // Mobile-first: the Mobile tab's base classes apply below lg; the Padding
    // tab's lg:-prefixed classes take over at lg+. Per-side overrides all-sides.
    blok.padding_mobile || '',
    blok.custom_padding_mobile_top || '',
    blok.custom_padding_mobile_right || '',
    blok.custom_padding_mobile_bottom || '',
    blok.custom_padding_mobile_left || '',
    blok.padding || '',
    blok.custom_padding_top || '',
    blok.custom_padding_right || '',
    blok.custom_padding_bottom || '',
    blok.custom_padding_left || '',
    blok.border_radius || '',
    hover.className,
  ].filter(Boolean).join(' ');

  const style = { ...colorStyle(blok), ...styleOverrides(blok), ...hover.style };

  // Position: relative anchors absolutely-positioned children; absolute pins the
  // box out of flow using the top/right/bottom/left offsets (in px).
  if (blok.position_absolute || blok.position_relative) {
    style.position = blok.position_absolute ? 'absolute' : 'relative';
    for (const [prop, val] of [['top', blok.pos_top], ['right', blok.pos_right], ['bottom', blok.pos_bottom], ['left', blok.pos_left]]) {
      if (val !== undefined && val !== null && val !== '') style[prop] = `${val}px`;
    }
  }

  return (
    <div {...storyblokEditable(blok)} className={className} style={style}>
      {blok.body?.map((nestedBlok) => (
        <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
      ))}
    </div>
  );
};

export default Box;
